import renderMathInElement from 'util/katex.js';

const CUSTOM_COLORS = {
    BrilliantLightBlue: '#95D3FE',
    BrilliantBlue: '#3D99F6',
    BrilliantDarkBlue: '#0C6AC7',
    BrilliantSeaGreen: '#1E93A5',
    BrilliantAqua: '#27D2E7',
    BrilliantLightGreen: '#67D94C',
    BrilliantGreen: '#20A900',
    BrilliantDarkGreen: '#456461',
    BrilliantYellow: '#CEBB00',
    BrilliantLightYellow: '#FEDF53',
    BrilliantYellowOrange: '#FCA04A',
    BrilliantOrange: '#EC7300',
    BrilliantRedOrange: '#FF664D',
    BrilliantRed: '#D61F06',
    BrilliantPink: '#E81990',
    BrilliantRosePink: '#F970BF',
    BrilliantViolet: '#BA33D6',
    BrilliantAmethyst: '#625CDB',
    BrilliantIndigo: '#302B94',
    BrilliantPurple: '#69047E',
    BrilliantPaleYellow: '#FFF7E6',
    BrilliantBeige: '#FBE6B8',
    BrilliantSandyBrown: '#FEDABE',
    BrilliantPeachy: '#FCC9A1',
    BrilliantTan: '#DBA97F',
    BrilliantLightBrown: '#B4825D',
    BrilliantMediumLightBrown: '#AA7956',
    BrilliantBronze: '#815331',
    BrilliantBrown: '#624F41',
    BrilliantWhite: '#FFFFFF',
    BrilliantLightGray: '#FBFBFB',
    BrilliantMediumLightGray: '#F0F0F0',
    BrilliantMediumGray: '#D8D8D8',
    BrilliantGray: '#BBBBBB',
    BrilliantMediumDarkGray: '#919191',
    BrilliantDarkGray: '#777777',
    BrilliantBlack: '#333333',
    BrilliantDarkBlack: '#161616',
};

// loading state flags
let mathjaxIsLoading = false;
let mathjaxIsLoaded = false;

// queue for holding elements that need to be rendered once mathjax has finished loading
let mathjaxRenderQueue = [];

// these will be called when MathJax is loaded
const callbacks = [];

// regexes for testing content for unrendered math
const regexInline = /\\\(.*?\\\)/;
const regexBlock = /\\\[.*?\\\]/;
const regexExclude = [
    /<script[\s\S]*?<\/script>/gm,
    /title=".*?"/g,
    /data-answers-list=".*?"/g,
    /data-registered-choice=".*?"/g,
    /<textarea[\s\S]*?<\/textarea>/gm,

    // ex: <span>\(</span>x^2<span>\)</span>
    // used in formatting guide to prevent rendering
    /<span>\\(\(|\)|\[|\])<\/span>/g,
];

/**
 * Check text for unrendered math
 * @param {String} txt
 * @return {Boolean} true if txt contains math
 */
export function textContainsUnrenderedMath(txt) {
    let newTxt = txt;
    for (let i = 0; i < regexExclude.length; i += 1) {
        newTxt = newTxt.replace(regexExclude[i], '');
    }
    return regexInline.test(newTxt) || regexBlock.test(newTxt);
}

/**
 * Check element for unrendered math
 * @param {HTMLElement} element
 * @return {Boolean} true if element contains math
 */
function elementContainsUnrenderedMath(element) {
    return textContainsUnrenderedMath(element.innerHTML);
}

function isLatexImageExport() {
    return window.location.pathname === '/latex-image-export/';
}

/**
 * Check if we should be using katex instead of mathjax
 * @returns {Boolean}
 */
export function useKatex() {
    return document.body.classList.contains('use_katex') && !isLatexImageExport();
}

/**
 * Render a single element
 * @param  {HTMLElement} element
 */
function renderElement(element) {
    if (useKatex()) {
        renderMathInElement(element);
        return;
    }
    try {
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, element]);
    } catch (e) {
        // ignore
    }
}

/**
 * Render each element in the queue
 */
function renderFromQueue() {
    mathjaxRenderQueue.forEach(renderElement);
    mathjaxRenderQueue = [];
}

/**
 * Load mathjax and render elements in the queue
 */
function loadMathjax() {
    if (mathjaxIsLoaded || mathjaxIsLoading) {
        return;
    }

    if (useKatex()) {
        mathjaxIsLoaded = true;
        return;
    }

    mathjaxIsLoading = true;

    const mjx = document.createElement('script');
    const config = isLatexImageExport() ? 'TeX-AMS_SVG' : 'TeX-AMS_HTML';
    mjx.type = 'text/javascript';
    mjx.src = `${document.body.dataset.mediaHost}/site_media/version-99mq9F5EZ8d8/js/mathjax/MathJax.js?config=${config}`;

    mjx.addEventListener('load', () => {
        if (!mathjaxIsLoaded) {
            mathjaxIsLoading = false;
            mathjaxIsLoaded = true;

            window.MathJax.Hub.Config({
                messageStyle: 'none',
                jax: [
                    'input/TeX',
                    'output/HTML-CSS',
                ],
                tex2jax: {
                    inlineMath: [['\\(', '\\)']],
                    displayMath: [['\\[', '\\]']],
                    processEnvironments: false,
                },
                TeX: {
                    extensions: [
                        'color.js',
                        'mhchem.js',
                        'siunitx.js',
                    ],
                    Macros: {
                        bra: ['{\\mathinner{\\langle{#1}|}}', 1],
                        ket: ['{\\mathinner{|{#1}\\rangle}}', 1],
                        braket: ['{\\mathinner{\\langle{#1}\\rangle}}', 1],
                        Bra: ['{\\left\\langle#1\\right|}', 1],
                        Ket: ['{\\left|#1\\right\\rangle}', 1],
                        ketbra: ['{\\ket{#1}\\bra{#2}}', 2],
                    },
                    Augment: {
                        Definitions: {
                            macros: {
                                overparen: ['UnderOver', '23DC'],
                                underparen: ['UnderOver', '23DD'],
                            },
                        },
                    },
                },
                'HTML-CSS': {
                    imageFont: null,
                },
                showMathMenu: false,
                // don't let MathJax reposition the window to #hash location from the page URL after
                // LaTeX is rendered (since it ruins our own scroll logic)
                positionToHash: false,
            });

            // remove pause between input and output phases to avoid jitter when updating output
            // frequently
            window.MathJax.Hub.processSectionDelay = 0;

            window.MathJax.Hub.Register.StartupHook('TeX color Ready', () => {
                Object.assign(window.MathJax.Extension['TeX/color'].colors, CUSTOM_COLORS);
            });

            callbacks.forEach(callback => callback());

            renderFromQueue();
        }
    });

    setTimeout(() => {
        const lastScript = document.getElementsByTagName('script')[0];
        lastScript.parentNode.insertBefore(mjx, lastScript);
    }, 0);
}

/**
 * If mathjax is not loaded, add element to rendering queue and trigger loading, if necessary.
 * Render element if mathjax is loaded
 * @param {HTMLElement} element
 */
function addToRenderQueue(element) {
    if (useKatex()) {
        setTimeout(() => renderMathInElement(element));
    } else if (mathjaxIsLoaded) {
        renderElement(element);
    } else {
        mathjaxRenderQueue.push(element);
        if (!mathjaxIsLoading) {
            loadMathjax();
        }
    }
}

/**
 * Preload mathjax when the user starts typing in a form input or textarea
 * @param {JQuery} $form
 */
export function formPreloader($form) {
    const $elements = $form.find('input[type=text], textarea');
    const evt = 'keydown.mjxpreload';
    $elements.on(evt, () => {
        $elements.off(evt);
        loadMathjax();
    });
}

/**
 * Check if an element needs mathjax and load it if it does
 * @param {JQuery} $elements
 * @param {boolean} [forceRender]
 */
export function loadForElement($elements, forceRender = false) {
    if (typeof $elements.each === 'function') {
        $elements.each((index, el) => {
            if (forceRender || elementContainsUnrenderedMath(el)) {
                addToRenderQueue(el);
            }
        });
    } else { // support non-jQuery as well
        const elements = Array.isArray($elements) ? $elements : [$elements];
        elements.forEach((el) => {
            if (forceRender || elementContainsUnrenderedMath(el)) {
                addToRenderQueue(el);
            }
        });
    }
}

/** Note that this handler is also used in the "content-team-visualizations" project */
export function onLoad(callback) {
    if (mathjaxIsLoaded) {
        callback();
    } else {
        callbacks.push(callback);
    }
}

export function loadForElementAsync($elements, forceRender = false) {
    return new Promise((resolve) => {
        if (useKatex()) {
            loadForElement($elements, forceRender);
            resolve();
        } else {
            onLoad(() => {
                setTimeout(() => {
                    loadForElement($elements, forceRender);
                    window.MathJax.Hub.Queue(() => resolve());
                });
            });
        }
    });
}

/**
 * Re-draw an existing expression. This is pretty specific to interactives, but I wanted to get
 * all of the MathJax.Hub code out of other files
 * @param {HTMLElement} element
 * @param {String} expression
 * @param {Function} drawFunc
 * @param {String} renderingClass
 */
export function redrawExpression(element, expression, drawFunc, renderingClass) {
    if (useKatex()) {
        return drawFunc(element, expression);
    }

    return new Promise((resolve) => {
        try {
            const jax = window.MathJax.Hub.getAllJax(element)[0];
            if (!jax) {
                element.classList.add(renderingClass);
                drawFunc(element, expression).then(resolve);
            } else {
                window.MathJax.Hub.Queue(['Text', jax, expression]);
                window.MathJax.Hub.Queue(() => {
                    resolve();
                });
            }
        } catch (err) {
            resolve();
        }
    });
}

/**
 * Load mathjax onload
 * Note that this controller is also used in the "content-team-visualizations" project
 */
export function controller() {
    const onload = () => addToRenderQueue(document.body);
    if (window.isLoaded) {
        onload();
    } else {
        window.addEventListener('load', onload);
    }
}
